import { useNavigate } from "react-router-dom";
import PageBanner from "@/components/feature/PageBanner";
import { useApi } from "@/hooks/useApi";
import type { FreePost } from "@/lib/types";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import { useAuth } from "@/components/feature/AuthProvider";
import FreeCard from "./components/FreeCard";

export default function FreePage() {
  const navigate = useNavigate();
  const { requireLogin } = useAuth();
  const { data, loading, error, reload } = useApi<FreePost[]>("frees.php?size=20");

  const posts = data ?? [];

  const handleWrite = () => {
    if (!requireLogin("/free/write")) return;
    navigate("/free/write");
  };

  return (
    <>
      <PageBanner
        title="자유글"
        description="회원들의 일상과 생각을 자유롭게 나누는 공간입니다."
        breadcrumb={[{ label: "자유글" }]}
      />

      <section className="mx-auto max-w-3xl px-4 md:px-6 py-8 md:py-10">
        <div className="mb-5 flex justify-end">
          <button
            type="button"
            onClick={handleWrite}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-quill-pen-line" aria-hidden="true"></i>
            글쓰기
          </button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="rounded-xl border border-background-200 bg-background-50 p-5">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-9 w-9 rounded-full" />
                  <div className="space-y-1.5">
                    <Skeleton className="h-3.5 w-24" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
                <Skeleton className="mt-4 h-4 w-3/4" />
                <Skeleton className="mt-2 h-3 w-full" />
              </div>
            ))}
          </div>
        ) : error ? (
          <EmptyState
            icon="ri-error-warning-line"
            title="자유글을 불러오지 못했습니다"
            description={error}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        ) : posts.length === 0 ? (
          <EmptyState
            icon="ri-chat-smile-3-line"
            title="아직 등록된 자유글이 없습니다"
            description="첫 번째 자유글을 남겨보세요."
            action={
              <button
                type="button"
                onClick={handleWrite}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-quill-pen-line" aria-hidden="true"></i>
                첫 글 남기기
              </button>
            }
          />
        ) : (
          <div className="space-y-3">
            {posts.map((post) => (
              <FreeCard key={post.no} post={post} onDeleted={reload} />
            ))}
          </div>
        )}
      </section>
    </>
  );
}